import React from 'react';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Location } from '../../index';
import { Box, Button, Tooltip } from '@material-ui/core';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
      margin: '0 auto',
      backgroundColor: theme.palette.background.default,
    },
    sectionTitle: {
      fontWeight: 500,
      fontSize: '11px',
      textTransform: 'uppercase' as const,
      letterSpacing: '1px',
      color: theme.palette.text.hint,
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(0.5),
      paddingLeft: theme.spacing(0.5),
      '&:first-child': {
        marginTop: 0,
      },
    },
    listItem: {
      padding: theme.spacing(1.2, 0.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    primaryText: {
      fontWeight: 600,
      fontSize: '15px',
      color: theme.palette.text.primary,
    },
    secondaryText: {
      fontSize: '13px',
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    chevronIcon: {
      color: theme.palette.text.hint,
      fontSize: '20px',
    },
    stickyButtonContainer: {
      position: 'fixed',
      top: theme.spacing(14),
      right: theme.spacing(8),
      zIndex: 1000,
    },
    secondStickyButtonContainer: {
      position: 'fixed',
      top: theme.spacing(22),
      right: theme.spacing(8),
      zIndex: 1000,
    },
    stickyButton: {
      paddingInline: theme.spacing(4),
    },
  });

interface ConfigurationOptionsProps extends WithStyles<typeof styles> {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setBreadcrumbs: any;
  location: Location;
  organisationId: string | undefined;
  locationId: string | undefined;
  isLocationCreate?: boolean;
  validationStatus: any;
  allStepsValid: boolean;
  handleSave: any;
}

const ConfigurationOptions: React.FC<ConfigurationOptionsProps> = ({
  classes,
  setStep,
  setBreadcrumbs,
  location,
  isLocationCreate = false,
  validationStatus,
  allStepsValid,
  handleSave,
  organisationId,
  locationId,
}) => {
  const sections = [
    {
      label: 'Store Setup',
      items: [
        {
          title: 'General',
          description: 'View and update your store details; contact info, physical address and marketing optin',
        },
        { title: 'Branding', description: 'Customise your store with branded assets and colour schemes' },
      ],
    },
    {
      label: 'Order Management',
      items: [
        { title: 'Opening Hours', description: 'Control when your customers can place orders' },
        { title: 'Operations', description: 'Manage how you receive, accept and process orders' },
        { title: 'Pre-Orders', description: 'Enable and manage pre-orders slot settings' },
        { title: 'Pacing', description: 'Adjust order pacing settings to optimize kitchen and service efficiency' },
      ],
    },
    ...(!isLocationCreate
      ? [
          {
            label: 'Business',
            items: [
              { title: 'Integrations', description: 'Set up Payments, Deliverect, Xero' },
              { title: 'Orders', description: 'Look the orders table, csv' },
              { title: 'Stock', description: 'Manage stock' },
              { title: 'Availability', description: 'Set day and time windows for menu item availability' },
            ],
          },
          {
            label: 'People & Access',
            items: [
              { title: 'Users', description: 'Location users' },
              ...(location.has_station_printers
                ? [
                    {
                      title: 'Multi station printing',
                      description: 'Manage LAN (WiFi / Network) printers',
                    },
                  ]
                : []),
              ...(location.has_staff ? [{ title: 'Staff', description: 'Manage staff for PIN access' }] : []),
              // @ts-ignore
              ...(location.integrations && location.integrations[0]?.vouchers
                ? [
                    {
                      title: 'Vouchers',
                      description: 'Discount voucher codes. Can be used for promotions or marketing',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  const flatOptions = sections.flatMap((section) => section.items);

  const handleClick = (flatIndex: number, title: string) => {
    // @ts-ignore
    setBreadcrumbs((prev) => [...prev, { label: title }]);
    setStep(flatIndex);
  };

  const handlePreviewLink = (mode: string) => {
    window.open(`/organisations/${organisationId}/locations/${locationId}/ordering/${mode}`);
  };

  return (
    <>
      {!isLocationCreate && (
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} style={{ gap: 16 }} mb={3}>
          <Button variant="contained" color="primary" className={classes.stickyButton} onClick={() => handlePreviewLink('menu1')}>
            Preview Store
          </Button>
          <Button variant="contained" color="primary" className={classes.stickyButton} onClick={() => handlePreviewLink('kiosk')}>
            Preview Kiosk
          </Button>
        </Box>
      )}
      <Paper className={classes.container}>
        {sections.map((section) => {
          if (section.items.length === 0) return null;
          return (
            <React.Fragment key={section.label}>
              <Typography className={classes.sectionTitle}>{section.label}</Typography>
              <List disablePadding>
                {section.items.map((option, itemIndex) => {
                  const flatIndex = flatOptions.indexOf(option);
                  const isLastInSection = itemIndex === section.items.length - 1;
                  return (
                    <ListItem
                      key={flatIndex}
                      className={classes.listItem}
                      onClick={() => handleClick(flatIndex + 1, option.title)}
                      style={{
                        backgroundColor: validationStatus[flatIndex + 1] === false ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                        borderBottom: isLastInSection ? 'none' : undefined,
                      }}>
                      <ListItemText
                        primary={option.title}
                        secondary={option.description}
                        primaryTypographyProps={{ className: classes.primaryText }}
                        secondaryTypographyProps={{ className: classes.secondaryText }}
                      />
                      <ListItemSecondaryAction>
                        <ChevronRightIcon className={classes.chevronIcon} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </React.Fragment>
          );
        })}
        {isLocationCreate && (
          <Tooltip title={!allStepsValid ? 'Complete all required sections before creating the location' : ''}>
            <span>
              <Button variant="contained" color="primary" disabled={!allStepsValid} onClick={handleSave} style={{ marginTop: '20px' }}>
                Create Location
              </Button>
            </span>
          </Tooltip>
        )}
      </Paper>
    </>
  );
};

export default withStyles(styles)(ConfigurationOptions);
